import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";
import { enumerer, pourcent } from "./cadres-textes";
import {
  CARTE,
  EnTete,
  FOCUS,
  LongOuCourt,
  NOTE,
  PETIT_BOUTON,
  PETIT_BOUTON_ETEINT,
} from "./cadres-ui";
import type { ApercuReponse, PlaqueApercu } from "./contract";
import { nombreFr } from "./silhouettes-textes";

/**
 * Les plaques une par une (#167, tache B6 ; decision 8 de la spec, maquette
 * `CadresPlaques.dc.html`). Une vignette par plaque, de l'avant vers le fond : la plaque telle
 * qu'elle sort du plateau, dans sa couleur, sur un damier la ou elle est trouee. Elles viennent
 * des images de l'apercu : aucune requete de plus.
 *
 * - Au survol ou au focus clavier d'une vignette, la page isole la plaque dans la perspective
 *   (`onSurvol`) : les autres plaques et le cadre s'estompent.
 * - Au clic ou a la touche Entree, la plaque s'agrandit dans une fenetre modale
 *   (`PlaqueAgrandie`) : les fleches passent a la voisine, Echap ferme, et le focus revient a
 *   la vignette qui l'a ouverte.
 */

/** « noir », ou « noir, au fond » pour une plaque de seconde passe. */
function nomDeLaPlaque(plaque: PlaqueApercu): string {
  return plaque.seconde_passe ? `${plaque.couleur}, au fond` : plaque.couleur;
}

function grammes(valeur: number): string {
  return `${nombreFr(valeur, 1)} g`;
}

function Pastille({ hexa, taille }: { hexa: string; taille: "petite" | "grande" }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block shrink-0 border border-border",
        taille === "petite" ? "size-3 rounded-[3px]" : "size-4 rounded",
      )}
      style={{ backgroundColor: hexa }}
    />
  );
}

export function CartePlaques({
  apercu,
  onSurvol,
  onOuvrir,
  className,
}: {
  apercu: ApercuReponse;
  /** La plaque survolee ou focalisee, a isoler dans la perspective ; `null` quand il n'y en a plus. */
  onSurvol: (rang: number | null) => void;
  onOuvrir: (rang: number) => void;
  className?: string;
}) {
  const id = useId();
  return (
    <section aria-labelledby={`${id}-titre`} className={cn(CARTE, className)}>
      <EnTete
        aparte="de l'avant vers le fond"
        id={`${id}-titre`}
        titre="Les plaques, une par une"
      />
      <p className="-mt-1.5 mb-3 text-[13px] text-muted-foreground">
        Chaque plaque telle qu'elle sort du plateau : sa couleur, et le damier là où elle est
        trouée.{" "}
        <LongOuCourt
          court="Touche une plaque pour l'agrandir."
          long="Survole une plaque pour la voir dans l'empilement ; clique pour l'agrandir."
        />
      </p>
      <ul aria-labelledby={`${id}-titre`} className="grid grid-cols-3 gap-3 md:grid-cols-4">
        {apercu.plaques.map((plaque) => (
          <li className="min-w-0" key={plaque.rang}>
            <button
              aria-label={`Plaque ${plaque.rang}, ${nomDeLaPlaque(plaque)}`}
              className={cn(
                "group flex w-full flex-col gap-[5px] rounded-md text-left text-[13px] text-foreground/80",
                FOCUS,
              )}
              data-vignette={plaque.rang}
              onBlur={() => onSurvol(null)}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                // Safari ne donne pas le focus a un bouton clique : la fenetre le rendra ici.
                event.currentTarget.focus();
                onOuvrir(plaque.rang);
              }}
              onFocus={() => onSurvol(plaque.rang)}
              onMouseEnter={() => onSurvol(plaque.rang)}
              onMouseLeave={() => onSurvol(null)}
              type="button"
            >
              <img
                alt=""
                className="damier block h-auto w-full rounded-md border border-border group-hover:outline-2 group-hover:outline-offset-2 group-hover:outline-foreground"
                draggable={false}
                src={`data:image/png;base64,${plaque.png}`}
              />
              <span className="flex min-w-0 items-center gap-1.5">
                <strong className="font-semibold text-foreground">{plaque.rang}</strong>
                <Pastille hexa={plaque.hexa} taille="petite" />
                <span className="truncate">{plaque.couleur}</span>
              </span>
              {/* « au fond » sur la ligne des grammes : sur celle du nom, une vignette de 95 px
                  le coupait en « noir, au f... ». Le nom accessible, lui, le porte en entier. */}
              <span className="-mt-[3px] text-[12.5px] text-muted-foreground">
                {plaque.seconde_passe ? "au fond · " : ""}
                {grammes(plaque.grammes)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Ce qu'on voit derriere la plaque, a travers ses trous : les couleurs des plaques suivantes. */
function derriere(plaque: PlaqueApercu, plaques: readonly PlaqueApercu[]): string {
  const suivantes = plaques.filter((autre) => autre.rang > plaque.rang);
  const [premiere] = suivantes;
  if (!premiere) return "C'est le fond : la plaque est pleine.";
  if (suivantes.length === 1) {
    return `Derrière, on voit la plaque ${premiere.rang} (${premiere.couleur}).`;
  }
  const couleurs = [...new Set(suivantes.map((autre) => autre.couleur))];
  const derniere = suivantes.at(-1)?.rang ?? premiere.rang;
  // Deux plaques : « 4 et 5 » ; au-dela, « 2 a 4 ».
  const lien = suivantes.length === 2 ? "et" : "à";
  return `Derrière, on voit les plaques ${premiere.rang} ${lien} ${derniere} (${enumerer(couleurs)}).`;
}

/** Ce qui peut prendre le focus dans la fenetre, dans l'ordre du document. */
const FOCALISABLES = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function PlaqueAgrandie({
  apercu,
  rang,
  onChanger,
  onFermer,
}: {
  apercu: ApercuReponse;
  rang: number;
  onChanger: (rang: number) => void;
  onFermer: () => void;
}) {
  const id = useId();
  const fenetre = useRef<HTMLDivElement>(null);
  const n = apercu.plaques.length;
  // Un rang qui n'existe plus (un recalcul en a donne moins) : la derniere plaque. La page le
  // borne deja ; les fleches et le titre partent de la plaque montree, jamais du rang recu.
  const plaque =
    apercu.plaques.find((candidate) => candidate.rang === rang) ?? apercu.plaques.at(-1);
  const [largeur, hauteur] = apercu.dimensions.plaques_mm;
  // Avec la LED, la plaque du fond est plus mince : `fond_mm` (0,6 mm), pas l'epaisseur des autres.
  const epaisseurDuFond =
    apercu.rapport.led === true && plaque?.rang === n && typeof apercu.rapport.fond_mm === "number"
      ? apercu.rapport.fond_mm
      : null;
  const epaisseur =
    epaisseurDuFond ??
    (typeof apercu.rapport.epaisseur_mm === "number" ? apercu.rapport.epaisseur_mm : null);

  // A l'ouverture, le focus entre dans la fenetre ; a la fermeture, il revient ou il etait (la
  // vignette qui l'a ouverte), s'il est toujours la.
  useEffect(() => {
    const origine = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    fenetre.current?.focus();
    return () => {
      if (origine?.isConnected) origine.focus();
    };
  }, []);

  if (!plaque) return null;
  const precedente = plaque.rang > 1 ? plaque.rang - 1 : null;
  const suivante = plaque.rang < n ? plaque.rang + 1 : null;

  const clavier = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      onFermer();
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const cible = event.key === "ArrowLeft" ? precedente : suivante;
      if (cible !== null) onChanger(cible);
      return;
    }
    if (event.key !== "Tab" || !fenetre.current) return;
    // Le focus reste dans la fenetre modale : du dernier controle, Tab revient au premier.
    const controles = Array.from(fenetre.current.querySelectorAll<HTMLElement>(FOCALISABLES));
    const premier = controles[0];
    const dernier = controles.at(-1);
    if (!premier || !dernier) return;
    const actif = document.activeElement;
    if (event.shiftKey && (actif === premier || actif === fenetre.current)) {
      event.preventDefault();
      dernier.focus();
    } else if (!event.shiftKey && actif === dernier) {
      event.preventDefault();
      premier.focus();
    }
  };

  const puces: ReactNode[] = [
    `${nombreFr(largeur, 1)} x ${nombreFr(hauteur, 1)}${epaisseur !== null ? ` x ${nombreFr(epaisseur, 1)}` : ""} mm`,
    grammes(plaque.grammes),
    `visible de face sur ${pourcent(plaque.visible_pct)} de la fenêtre`,
    plaque.parties_fines > 0
      ? `${plaque.parties_fines} partie${plaque.parties_fines > 1 ? "s" : ""} fine${plaque.parties_fines > 1 ? "s" : ""}, en rose`
      : "aucune partie fine",
  ];

  return createPortal(
    // Le voile : un clic dessus ferme la fenetre. Le clavier a Echap et le bouton Fermer.
    // biome-ignore lint/a11y/noStaticElementInteractions: le voile n'est pas un controle ; le clavier ferme par Echap, dans la fenetre
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/45 p-4 md:items-center md:p-8"
      data-testid="voile"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onFermer();
      }}
    >
      <div
        aria-labelledby={`${id}-titre`}
        aria-modal="true"
        className={cn(
          "w-full max-w-3xl rounded-xl border border-border bg-card p-5 text-card-foreground shadow-2xl",
          "focus-visible:outline-none",
        )}
        onKeyDown={clavier}
        ref={fenetre}
        role="dialog"
        tabIndex={-1}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2
              className="text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground"
              id={`${id}-titre`}
            >
              Plaque {plaque.rang} sur {n}
            </h2>
            <p className="flex items-center gap-2 text-lg font-semibold">
              <Pastille hexa={plaque.hexa} taille="grande" />
              {nomDeLaPlaque(plaque)}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              aria-disabled={precedente === null || undefined}
              aria-label={
                precedente === null ? "Plaque précédente" : `Plaque précédente, ${precedente}`
              }
              className={precedente === null ? PETIT_BOUTON_ETEINT : PETIT_BOUTON}
              onClick={() => {
                if (precedente !== null) onChanger(precedente);
              }}
              type="button"
            >
              ← {precedente ?? ""}
            </button>
            <button
              aria-disabled={suivante === null || undefined}
              aria-label={suivante === null ? "Plaque suivante" : `Plaque suivante, ${suivante}`}
              className={suivante === null ? PETIT_BOUTON_ETEINT : PETIT_BOUTON}
              onClick={() => {
                if (suivante !== null) onChanger(suivante);
              }}
              type="button"
            >
              {suivante ?? ""} →
            </button>
            <button className={PETIT_BOUTON} onClick={onFermer} type="button">
              Fermer
            </button>
          </div>
        </div>

        <div className="relative mx-auto mt-4 w-fit max-w-full">
          <img
            alt={`La plaque ${plaque.rang} agrandie`}
            className="damier block h-auto max-h-[60vh] w-auto max-w-full min-w-[min(100%,16rem)] rounded-lg border border-border"
            draggable={false}
            src={`data:image/png;base64,${plaque.png}`}
          />
          {plaque.fines_png ? (
            <img
              alt="Parties fines, en rose"
              className="pointer-events-none absolute inset-0 size-full rounded-lg"
              draggable={false}
              src={`data:image/png;base64,${plaque.fines_png}`}
            />
          ) : null}
        </div>

        <ul aria-label="Chiffres de la plaque" className="mt-3.5 flex flex-wrap gap-2">
          {puces.map((puce) => (
            <li className="rounded-full bg-muted px-2.5 py-1 text-[13.5px]" key={String(puce)}>
              {puce}
            </li>
          ))}
        </ul>

        <p className={cn(NOTE, "mt-3.5")}>
          {derriere(plaque, apercu.plaques)} Les parties fines d'une plaque, s'il y en a, sont
          entourées en rose.
        </p>

        <ul aria-label="Toutes les plaques" className="mt-4 flex flex-wrap gap-2">
          {apercu.plaques.map((autre) => (
            <li key={autre.rang}>
              <button
                aria-current={autre.rang === plaque.rang ? "true" : undefined}
                aria-label={`Plaque ${autre.rang}, ${nomDeLaPlaque(autre)}`}
                className={cn(
                  "block rounded-[5px]",
                  FOCUS,
                  autre.rang === plaque.rang &&
                    "outline-2 outline-offset-2 outline-foreground outline",
                )}
                onClick={() => onChanger(autre.rang)}
                type="button"
              >
                <img
                  alt=""
                  className="damier size-14 rounded-[5px] border border-input object-contain"
                  draggable={false}
                  src={`data:image/png;base64,${autre.png}`}
                />
              </button>
            </li>
          ))}
        </ul>
        <p className="mt-2.5 text-[13px] text-muted-foreground">
          Flèches du clavier pour passer d'une plaque à l'autre, Échap pour fermer.
        </p>
      </div>
    </div>,
    document.body,
  );
}
